import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const WebMaintenance = () => {
  return (
    <div className="web-maintenance-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1>
            <span className="gradient-text">Web Maintenance</span> Services
          </h1>
          <p className="subtitle">
            24/7 monitoring with automated alerts, zero-downtime deployments, and performance audits
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Proactive <span className="highlight">Website Care</span></h2>
            <p>Comprehensive maintenance solutions to keep your site secure and performing at its best</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">👁️</div>
              <h3>24/7 Monitoring</h3>
              <ul>
                <li>Real-time uptime monitoring</li>
                <li>Performance tracking</li>
                <li>Automated alert system</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Zero-Downtime Updates</h3>
              <ul>
                <li>Seamless CMS updates</li>
                <li>Plugin/extension maintenance</li>
                <li>Staging environment testing</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Security Maintenance</h3>
              <ul>
                <li>Malware scanning</li>
                <li>Vulnerability patches</li>
                <li>Firewall management</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Performance Optimization</h3>
              <ul>
                <li>Monthly speed audits</li>
                <li>Database optimization</li>
                <li>Caching configuration</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>Content Updates</h3>
              <ul>
                <li>Text/image updates</li>
                <li>SEO content refreshes</li>
                <li>Broken link fixes</li>
              </ul>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Monthly Reports</h3>
              <ul>
                <li>Performance metrics</li>
                <li>Security status</li>
                <li>Recommendations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring Dashboard Preview */}
      <section className="dashboard-section">
        <div className="container">
          <div className="section-header">
            <h2>Real-time <span className="highlight">Monitoring</span></h2>
            <p>Our dashboard gives you complete visibility into your website's health</p>
          </div>
          <div className="dashboard-preview">
            <div className="dashboard-mockup">
              <div className="metric">Uptime: 99.99%</div>
              <div className="metric">Response: 218ms</div>
              <div className="metric">Last Scan: Clean</div>
              <div className="metric">Visitors: 2,841</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <ModernCTA />
      </section>

      {/* Styles */}
      <style>{`
        .web-maintenance-page {
          margin-top: 80px;
          color: #f8fafc;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        /* Hero Section */
        .hero-section {
          padding: 60px 0;
          text-align: center;
        }
        
        .hero-section h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
          line-height: 1.2;
        }
        
        .gradient-text {
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .subtitle {
          font-size: 1.1rem;
          color: #94a3b8;
          max-width: 700px;
          margin: 0 auto;
          padding: 0 15px;
        }
        
        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .section-header h2 {
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 0.8rem;
          padding: 0 10px;
        }
        
        .section-header p {
          color: #94a3b8;
          font-size: 1rem;
          max-width: 700px;
          margin: 0 auto;
          padding: 0 15px;
        }
        
        .highlight {
          color: #818cf8;
        }
        
        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }
        
        .feature-card {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          padding: 25px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-5px);
        }
        
        .feature-icon {
          font-size: 1.8rem;
          margin-bottom: 15px;
        }
        
        .feature-card h3 {
          font-size: 1.3rem;
          margin-bottom: 0.8rem;
        }
        
        .feature-card ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .feature-card li {
          position: relative;
          padding-left: 22px;
          margin-bottom: 8px;
          color: #cbd5e1;
          font-size: 0.95rem;
        }
        
        .feature-card li::before {
          content: '•';
          color: #818cf8;
          position: absolute;
          left: 0;
          font-size: 1.1rem;
        }
        
        /* Dashboard Section */
        .dashboard-section {
          padding: 60px 0;
        }
        
        .dashboard-preview {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .dashboard-mockup {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          background: rgba(15, 23, 42, 0.5);
          padding: 20px;
          border-radius: 8px;
        }
        
        .metric {
          background: rgba(99, 102, 241, 0.1);
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .web-maintenance-page {
            margin-top: 70px;
          }
          
          .hero-section {
            padding: 50px 0;
          }
          
          .hero-section h1 {
            font-size: 2rem;
            padding: 0 10px;
          }
          
          .subtitle {
            font-size: 1rem;
          }
          
          .section-header h2 {
            font-size: 1.8rem;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
          }
          
          .dashboard-mockup {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 480px) {
          .hero-section h1 {
            font-size: 1.8rem;
          }
          
          .section-header h2 {
            font-size: 1.6rem;
          }
          
          .dashboard-mockup {
            grid-template-columns: 1fr;
          }
          
          .feature-card {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default WebMaintenance;